/**
 * hooks/useUsersList.ts
 *
 * Loads the list of non-admin users for the admin catalog context selector.
 * Returns id, email, first_name, last_name, invitation_status, invited_at for each user.
 * Created: 2026-03-12 - initial version for admin catalog user view
 * Updated: 2026-03-25 - added invitation_status and invited_at for admin user management
 */

import { useState, useEffect } from 'react';
import { supabase } from '../lib/supabase';
import { captureError } from '../utils/errorReporting';

export type InvitationStatus = 'pending' | 'accepted' | null;

export interface UserListItem {
  id: string;
  email: string;
  first_name: string | null;
  last_name: string | null;
  invitation_status: InvitationStatus;
  invited_at: string | null;
}

export function getUserDisplayName(u: UserListItem): string {
  const first = u.first_name?.trim() ?? '';
  const last = u.last_name?.trim() ?? '';
  if (first || last) return [first, last].filter(Boolean).join(' ');
  return u.email.split('@')[0];
}

export function getUserInitials(u: UserListItem): string {
  const first = u.first_name?.trim() ?? '';
  const last = u.last_name?.trim() ?? '';
  if (first && last) return (first[0] + last[0]).toUpperCase();
  if (first) return first.slice(0, 2).toUpperCase();
  if (last) return last.slice(0, 2).toUpperCase();
  return u.email.slice(0, 2).toUpperCase();
}

export function getColorFromEmail(email: string): string {
  const colors = [
    'bg-blue-500',
    'bg-green-500',
    'bg-pink-500',
    'bg-orange-500',
    'bg-teal-500',
    'bg-cyan-500',
    'bg-emerald-500',
    'bg-rose-500',
  ];
  const hash = email.split('').reduce((acc, char) => acc + char.charCodeAt(0), 0);
  return colors[hash % colors.length];
}

export function useUsersList() {
  const [users, setUsers] = useState<UserListItem[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchUsers();
  }, []);

  async function fetchUsers() {
    try {
      setLoading(true);
      const { data, error } = await supabase
        .from('users')
        .select('id, email, first_name, last_name, invitation_status, invited_at')
        .eq('is_admin', false)
        .order('email', { ascending: true });

      if (error) throw error;
      setUsers(data ?? []);
    } catch (err) {
      console.error('Error loading users list:', err);
      captureError(err, { hook: 'useUsersList', action: 'fetchUsers' });
    } finally {
      setLoading(false);
    }
  }

  return { users, loading };
}
